import React from "react";
import styled from "styled-components/macro";
import { useViewerDecks } from "queries/viewerQuery";
import { AssistantMode } from "../__generated__/AssistantMutation.graphql";
import { useGetSuggestions } from "./suggestionsHooks";
import Toggler from "../../other/Toggler";
import { Stack } from "@mui/material";
import { TranslatedSuggestion } from "./suggestionsDefinitions";
import ChatSuggestion from "./ChatSuggestion";
import { usePrevious } from "react-use";

const TWO_ROWS_TRESHOLD = 4;

const SliderWrap = styled(Stack)`
  width: 100%;
  overflow-x: auto;
  background: ${(p) => p.theme.duo.color.secondLevelBg};
  padding: 14px 20px;
  position: relative;
`;
const SStack = styled(Stack)`
  &:after {
    content: "_";
    opacity: 0;
  }
`;

type Props = {
  onUseSuggestion: (suggestion: TranslatedSuggestion) => void;
  messagesCount: number;
  chatMode: AssistantMode;
};
const ChatSuggestions: React.FC<Props> = React.memo(({ onUseSuggestion, messagesCount, chatMode }) => {
  const { deck } = useViewerDecks();
  const [expanded, setExpanded] = React.useState(false);
  const { isLoading, translatedSuggestions } = useGetSuggestions(
    chatMode,
    messagesCount,
    deck?.back || "en",
    deck?.front || "en"
  );

  const canShow = expanded;
  const previousSuggestions = usePrevious(translatedSuggestions);
  const currentSuggestions = expanded ? translatedSuggestions : previousSuggestions || []; // keeps suggestions in the box when toggler closes

  const firstLine =
    currentSuggestions.length < TWO_ROWS_TRESHOLD
      ? currentSuggestions
      : currentSuggestions.filter((s, i) => i % 2 === 0);
  const secondLine =
    currentSuggestions.length < TWO_ROWS_TRESHOLD ? [] : currentSuggestions.filter((s, i) => i % 2 === 1);

  React.useEffect(() => {
    setExpanded(!isLoading && !!translatedSuggestions.length);
  }, [isLoading, translatedSuggestions.length]);

  const handleClick = (suggestion: TranslatedSuggestion) => {
    setExpanded(false);
    onUseSuggestion(suggestion);
  };

  return (
    <Toggler show={canShow}>
      <SliderWrap direction="column" spacing={2}>
        <SStack direction="row" spacing={2}>
          {firstLine.map((suggestion, i) => (
            <ChatSuggestion key={i} onClick={handleClick} suggestion={suggestion} />
          ))}
        </SStack>
        {!!secondLine.length && (
          <SStack direction="row" spacing={2}>
            {secondLine.map((suggestion, i) => (
              <ChatSuggestion key={i} onClick={handleClick} suggestion={suggestion} />
            ))}
          </SStack>
        )}
      </SliderWrap>
    </Toggler>
  );
});

export default ChatSuggestions;
